/* Public multi-step offer application flow.
   URL: /offer-apply/:publisherId
   Step 1: Traffic Source · Step 2: Select Offer · Step 3: Proofs
   Traffic-source questionnaire (fields, labels, descriptions and proof
   image rules) is imported from ../config/trafficSources so it stays in
   lock-step with the primary publisher onboarding form. */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Upload,
  CheckCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Phone,
  X,
  Image as ImageIcon,
  FileText,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  PRIMARY_SOURCES,
  sourceByKey,
  PROOF_MAX_FILES,
  PROOF_MIN_FILES,
  PROOF_MAX_BYTES,
  PROOF_ALLOWED_MIME,
} from "../config/trafficSources";
import { MultiStateSelect } from "../components/MultiStateSelect";

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Sub-components ──────────────────────────────────────────────── */

const ProgressBar = ({ step }) => (
  <div className="w-full max-w-3xl mx-auto mb-8" data-testid="progress-bar">
    <div className="flex items-center justify-between mb-2">
      {[1, 2, 3].map((s) => {
        const active = s <= step;
        const labels = ["Traffic Source", "New Offer", "Upload Proofs"];
        return (
          <div key={s} className="flex-1 flex items-center">
            <div className={`flex items-center gap-2 ${active ? "text-emerald-300" : "text-slate-500"}`}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                  active
                    ? "bg-gradient-to-br from-blue-500 to-purple-600 border-transparent text-white"
                    : "bg-slate-900 border-slate-700 text-slate-500"
                }`}
              >
                {active && s < step ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              <span className="text-xs uppercase tracking-widest font-semibold hidden sm:inline">
                {labels[s - 1]}
              </span>
            </div>
            {s < 3 && <div className={`flex-1 h-0.5 mx-2 ${s < step ? "bg-emerald-400" : "bg-slate-800"}`} />}
          </div>
        );
      })}
    </div>
  </div>
);

const StepShell = ({ title, subtitle, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    className="bg-slate-900/70 backdrop-blur border border-slate-800 rounded-2xl p-5 sm:p-8 shadow-2xl"
  >
    <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-1">{title}</h2>
    {subtitle && <p className="text-slate-400 text-sm mb-6">{subtitle}</p>}
    {children}
  </motion.div>
);

const TrafficSourceStep = ({ selected, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="traffic-source-grid">
    {PRIMARY_SOURCES.map((s) => {
      const Icon = s.icon;
      const active = selected === s.key;
      return (
        <button
          key={s.key}
          type="button"
          onClick={() => onSelect(s.key)}
          data-testid={`traffic-${s.key}`}
          className={`group text-left rounded-xl border p-4 transition-all ${
            active
              ? "border-emerald-400 bg-emerald-500/5 ring-2 ring-emerald-500/40"
              : "border-slate-800 bg-slate-800/40 hover:border-slate-600"
          }`}
        >
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
            <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="text-white font-semibold text-sm">{s.label}</div>
          <div className="text-slate-500 text-xs mt-0.5">{s.tagline}</div>
          {active && (
            <div className="text-emerald-400 text-[11px] font-semibold uppercase tracking-widest mt-2 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" /> Selected
            </div>
          )}
        </button>
      );
    })}
  </div>
);

const OfferStep = ({ offers, selectedOfferId, onSelect }) => {
  if (!offers.length) {
    return (
      <div className="text-slate-400 text-sm bg-slate-800/40 border border-slate-800 rounded-lg p-4">
        No offers are active right now. Please contact your Publisher Manager.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="offer-grid">
      {offers.map((o) => {
        const active = selectedOfferId === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onSelect(o)}
            data-testid={`offer-${o.id}`}
            className={`text-left rounded-xl border p-4 transition-all ${
              active
                ? "border-emerald-400 bg-emerald-500/5 ring-2 ring-emerald-500/40"
                : "border-slate-800 bg-slate-800/40 hover:border-slate-600"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm truncate">{o.name}</div>
                <div className="text-slate-500 text-xs mt-1">
                  {o.call_type || "General"} · {o.buffer_time || "120"}s buffer · {o.payment_terms || "Net 10"}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-emerald-400 font-bold text-lg tabular-nums">
                  {o.is_fixed ? "$" : "Up to $"}{o.payout || "0"}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">per call</div>
              </div>
            </div>
            {o.description && (
              <div className="text-slate-400 text-xs mt-2 line-clamp-2">{o.description}</div>
            )}
            {active && (
              <div className="text-emerald-400 text-[11px] font-semibold uppercase tracking-widest mt-2 flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" /> Selected
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

const ProofStep = ({ source, values, setValues, files, setFiles, previews, setPreviews }) => {
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState("");
  if (!source) return null;
  const set = (name) => (e) => setValues({ ...values, [name]: e.target.value });

  const addFiles = (raw) => {
    const remaining = PROOF_MAX_FILES - files.length;
    const trimmed = Array.from(raw).slice(0, remaining);
    const ok = trimmed.filter(
      (f) => PROOF_ALLOWED_MIME.includes(f.type) && f.size <= PROOF_MAX_BYTES
    );
    if (ok.length !== trimmed.length) {
      setFileError("Only JPG / PNG / WEBP / HEIC / PDF up to 10 MB per file.");
    } else {
      setFileError("");
    }
    const nextFiles = [...files, ...ok].slice(0, PROOF_MAX_FILES);
    setFiles(nextFiles);
    Promise.all(
      nextFiles.map(
        (f) =>
          new Promise((res) => {
            if (f.type === "application/pdf") return res({ pdf: true, name: f.name });
            const r = new FileReader();
            r.onload = () => res({ url: r.result, name: f.name });
            r.readAsDataURL(f);
          })
      )
    ).then(setPreviews);
  };

  const removeFile = (i) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
    setPreviews((prev) => prev.filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-5" data-testid="proof-step">
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-blue-200 text-xs">
        <strong className="uppercase tracking-widest text-[10px]">Required for {source.label}:</strong>{" "}
        {source.proofHint}
      </div>

      {/* Same set of source-specific fields as the primary onboarding form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="source-fields-grid">
        {source.fields.map((f) => {
          const inputClass =
            "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2.5 text-slate-100 text-sm placeholder-slate-500 focus:border-blue-400 focus:outline-none";
          return (
            <div key={f.name} className={f.textarea ? "sm:col-span-2" : ""}>
              <label className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">
                {f.label}
                {f.required && (
                  <span className="ml-1 text-red-400" data-testid={`required-${f.name}`}>*&nbsp;Required</span>
                )}
              </label>
              {f.multiSelect ? (
                <MultiStateSelect
                  dark
                  options={f.options || []}
                  values={Array.isArray(values[f.name]) ? values[f.name] : []}
                  onChange={(arr) => setValues({ ...values, [f.name]: arr })}
                  testid={`proof-field-${f.name}`}
                />
              ) : f.type === "file" ? (
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf"
                    className="hidden"
                    id={`proof-source-file-${f.name}`}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setValues({ ...values, [f.name]: file.name });
                        addFiles([file]);
                      }
                      if (e.target) e.target.value = "";
                    }}
                    data-testid={`proof-field-${f.name}`}
                  />
                  <label
                    htmlFor={`proof-source-file-${f.name}`}
                    className="inline-flex items-center gap-2 px-3 py-2.5 border border-dashed border-slate-600 rounded-md text-sm font-medium text-slate-300 hover:border-blue-400 hover:text-blue-300 cursor-pointer transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    {values[f.name] ? `Attached: ${values[f.name]}` : "Choose screenshot…"}
                  </label>
                </div>
              ) : f.textarea ? (
                <textarea
                  rows={3}
                  required={f.required}
                  value={values[f.name] || ""}
                  onChange={set(f.name)}
                  placeholder={f.placeholder}
                  className={inputClass}
                  data-testid={`proof-field-${f.name}`}
                />
              ) : (
                <input
                  type={f.type || "text"}
                  required={f.required}
                  value={values[f.name] || ""}
                  onChange={set(f.name)}
                  placeholder={f.placeholder}
                  className={inputClass}
                  data-testid={`proof-field-${f.name}`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Multi-file proof upload — same rules as the primary form */}
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">
          {source.label} Proof &amp; Screenshots
          <span className="ml-1 text-red-400" data-testid="required-proofs">*&nbsp;Required · at least {PROOF_MIN_FILES}</span>
          <span className="ml-2 text-slate-500 normal-case tracking-normal">
            (up to {PROOF_MAX_FILES} images / PDFs, 10 MB each)
          </span>
        </label>
        {files.length < PROOF_MAX_FILES && (
          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
            }}
            className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
              dragOver
                ? "border-blue-400 bg-blue-500/10"
                : "border-slate-700 hover:border-slate-500 bg-slate-800/40"
            }`}
            data-testid="proof-dropzone"
          >
            <input
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf"
              className="hidden"
              onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }}
              data-testid="proof-file-input"
            />
            <Upload className="w-7 h-7 text-slate-500" />
            <div className="text-sm text-slate-300">
              Click to upload — {PROOF_MAX_FILES - files.length} more allowed
              <span className="text-slate-500"> (or drag &amp; drop)</span>
            </div>
            <div className="text-[11px] text-slate-500">JPG / PNG / WEBP / HEIC / PDF · max 10 MB each</div>
          </label>
        )}

        {previews.length > 0 && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="proof-previews">
            {previews.map((p, i) => (
              <div key={i} className="relative rounded-lg border border-slate-700 bg-slate-800/60 overflow-hidden group">
                {p.pdf ? (
                  <div className="flex items-center gap-2 p-4 text-slate-300 text-xs">
                    <FileText className="w-6 h-6 text-red-400" />
                    <span className="font-mono truncate">{p.name}</span>
                  </div>
                ) : (
                  <img src={p.url} alt={p.name} className="w-full h-28 object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute top-1 right-1 bg-slate-900/80 hover:bg-red-500/90 text-white rounded-full p-1"
                  data-testid={`proof-remove-${i}`}
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <div className="px-2 py-1 text-[10px] text-slate-400 font-mono truncate bg-slate-900/60">
                  {p.name}
                </div>
              </div>
            ))}
          </div>
        )}

        {fileError && (
          <div className="mt-2 text-red-400 text-xs" data-testid="proof-file-error">{fileError}</div>
        )}
      </div>

      {/* No-random-images rule */}
      <div className="flex items-start gap-2.5 rounded-lg border border-red-500/40 bg-red-500/10 p-3" data-testid="proof-random-images-warning">
        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
        <p className="text-xs text-red-300 leading-relaxed">
          <span className="font-bold">Warning:</span> Only upload relevant <span className="font-semibold">{source.label}</span> screenshots.
          Random images or stock photos will result in an <span className="font-bold">immediate ban</span> from our network.
        </p>
      </div>
    </div>
  );
};

/* ─── Page ───────────────────────────────────────────────────────── */

export default function OfferApplicationPage() {
  const { publisherId } = useParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [publisher, setPublisher] = useState(null);
  const [offers, setOffers] = useState([]);
  const [sourceKey, setSourceKey] = useState("");
  const [chosenOffer, setChosenOffer] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [proofFiles, setProofFiles] = useState([]);
  const [proofPreviews, setProofPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  // Auto-scroll to the top of the page each time the step changes so the
  // publisher lands on the header, not the footer.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, submitted]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/apply/publisher/${publisherId}/context`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setPublisher(data.publisher);
        // Highest-paying first — matches the exact ordering on /offers.
        const sorted = [...(data.offers || [])].sort((a, b) => {
          const pa = parseFloat(String(a.payout || 0).replace(/[^\d.]/g, "")) || 0;
          const pb = parseFloat(String(b.payout || 0).replace(/[^\d.]/g, "")) || 0;
          return pb - pa;
        });
        setOffers(sorted);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [publisherId]);

  const source = useMemo(() => sourceByKey(sourceKey), [sourceKey]);

  const canProceedStep1 = !!sourceKey;
  const canProceedStep2 = !!chosenOffer;

  // Match the primary form's validation: every required text field non-empty
  // AND at least PROOF_MIN_FILES uploaded.
  const missingFieldLabels = useMemo(() => {
    if (!source) return [];
    return source.fields
      .filter((f) => {
        if (!f.required) return false;
        if (f.type === "file") return false; // covered by the mandatory proof upload
        if (f.multiSelect) {
          return !(Array.isArray(fieldValues[f.name]) && fieldValues[f.name].length > 0);
        }
        return !(fieldValues[f.name] || "").toString().trim();
      })
      .map((f) => f.label);
  }, [source, fieldValues]);
  const missingProofs = proofFiles.length < PROOF_MIN_FILES;
  const canSubmit = source && chosenOffer && missingFieldLabels.length === 0 && !missingProofs;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("publisher_id", publisherId);
      fd.append("traffic_source_key", source.key);
      fd.append("traffic_source_label", source.label);
      fd.append("offer_id", chosenOffer.id);
      fd.append("offer_name", chosenOffer.name);
      fd.append("source_fields", JSON.stringify(fieldValues));
      proofFiles.forEach((pf) => fd.append("proof_files", pf));
      const res = await fetch(`${API}/api/apply/offer-request`, { method: "POST", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSubmitted(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setSubmitted(null);
    setStep(1);
    setSourceKey("");
    setChosenOffer(null);
    setFieldValues({});
    setProofFiles([]);
    setProofPreviews([]);
    setError("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <Header />
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/50 text-emerald-200 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-bold mb-3" data-testid="active-publisher-badge">
            <CheckCircle className="w-3.5 h-3.5" /> Active Publisher
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight" data-testid="welcome-heading">
            Welcome Back,{" "}
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              {publisher?.name || "Publisher"}
            </span>
          </h1>
          <p className="text-slate-300 text-base mt-3" data-testid="welcome-subheading">
            Select your traffic source and the new offer you want to run.
          </p>
          {publisher && (
            <p className="text-slate-500 text-xs mt-2 font-mono" data-testid="publisher-header">
              {publisher.email}
            </p>
          )}
        </div>

        {loading ? (
          <div className="max-w-3xl mx-auto flex items-center justify-center gap-2 text-slate-400 text-sm py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your application…
          </div>
        ) : error && !publisher ? (
          <div className="max-w-3xl mx-auto bg-red-500/10 border border-red-500/40 text-red-300 rounded-xl p-6 text-center" data-testid="load-error">
            <div className="text-lg font-semibold mb-1">Application link invalid</div>
            <div className="text-sm">{error}</div>
            <Link to="/" className="inline-block mt-4 text-blue-300 underline text-sm">Return home</Link>
          </div>
        ) : submitted ? (
          <div className="max-w-3xl mx-auto bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-8 text-center" data-testid="submit-success">
            <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Offer Request Submitted</h2>
            <p className="text-slate-300 text-sm mb-6">
              We've received your application for{" "}
              <span className="text-emerald-400 font-semibold">{submitted.offer_name}</span>{" "}
              on{" "}
              <span className="text-white font-semibold">{submitted.traffic_source_label}</span>.
              Your Publisher Manager will review it within one business day.
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 rounded-lg border border-slate-700"
              data-testid="submit-another"
            >
              Apply for another offer
            </button>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto">
            <ProgressBar step={step} />

            {step === 1 && (
              <StepShell
                title="Step 1 · Choose your Traffic Source"
                subtitle="Tell us how you'll drive calls. This decides what proofs we'll need on Step 3."
              >
                <TrafficSourceStep selected={sourceKey} onSelect={setSourceKey} />
                <div className="flex justify-end mt-8">
                  <button
                    type="button"
                    disabled={!canProceedStep1}
                    onClick={() => setStep(2)}
                    className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm px-5 py-2.5 rounded-lg"
                    data-testid="step1-next"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </StepShell>
            )}

            {step === 2 && (
              <StepShell
                title="Step 2 · Select Offer"
                subtitle="These are the live offers you can run right now."
              >
                <OfferStep
                  offers={offers}
                  selectedOfferId={chosenOffer?.id}
                  onSelect={setChosenOffer}
                />
                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-500 text-slate-200 hover:text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
                    data-testid="step2-back"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    type="button"
                    disabled={!canProceedStep2}
                    onClick={() => setStep(3)}
                    className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm px-5 py-2.5 rounded-lg"
                    data-testid="step2-next"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </StepShell>
            )}

            {step === 3 && (
              <StepShell
                title="Step 3 · Submit your Proofs"
                subtitle={`Provide the exact same details we ask on our main publisher form — every field is required for ${source?.label || "your source"}.`}
              >
                <ProofStep
                  source={source}
                  values={fieldValues}
                  setValues={setFieldValues}
                  files={proofFiles}
                  setFiles={setProofFiles}
                  previews={proofPreviews}
                  setPreviews={setProofPreviews}
                />
                {error && (
                  <div className="mt-4 bg-red-500/10 border border-red-500/40 text-red-300 rounded-lg p-3 text-sm" data-testid="submit-error">
                    {error}
                  </div>
                )}
                {!canSubmit && (missingFieldLabels.length > 0 || missingProofs) && (
                  <div className="mt-4 bg-amber-500/10 border border-amber-500/40 text-amber-200 rounded-lg p-3 text-xs" data-testid="validation-summary">
                    Still needed:
                    <ul className="mt-1 list-disc list-inside space-y-0.5">
                      {missingFieldLabels.map((l) => <li key={l}>{l}</li>)}
                      {missingProofs && <li>At least {PROOF_MIN_FILES} proof screenshot / PDF</li>}
                    </ul>
                  </div>
                )}
                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-500 text-slate-200 hover:text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
                    data-testid="step3-back"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    type="button"
                    disabled={!canSubmit || submitting}
                    onClick={submit}
                    className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm px-5 py-2.5 rounded-lg"
                    data-testid="submit-application"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                    ) : (
                      <>Submit Application <CheckCircle className="w-4 h-4" /></>
                    )}
                  </button>
                </div>
              </StepShell>
            )}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
